import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FaCog, FaPlus, FaTrash, FaEdit, FaBook, FaGlobe, FaQuestionCircle, FaChevronDown, FaChevronUp, FaComments, FaUpload } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { API } from '../App';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { COUNTRIES, LANGUAGES, getUserCountry, getUserLanguage, setUserLanguage, updateUserPreferences, refreshUserLocation, detectBrowserLanguage } from '../utils/geolocation';
import OnboardingTooltip from '../components/OnboardingTooltip';
import { resetAllTours, resetTour, getSettingsPageSteps, isTourCompleted, markTourCompleted, TOUR_KEYS } from '../utils/onboarding';
import { isFeatureEnabled } from '../utils/featureFlags';
import SubscriptionSection from '../components/SubscriptionSection';
import { useTranslation } from 'react-i18next';
import { hasPremiumAccess, getUserSubscription } from '../config/subscriptions';

const SettingsPage = ({ sessionId }) => {
  const { user, isAdmin, isPro, isEditor, updateCompletedTours } = useAuth();
  const { t } = useTranslation();
  const [machines, setMachines] = useState([]);
  const [userRecipesCount, setUserRecipesCount] = useState(0);
  const [canAddRecipe, setCanAddRecipe] = useState(true);
  const [limitMessage, setLimitMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [isAddMachineOpen, setIsAddMachineOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editMachineId, setEditMachineId] = useState(null);
  const [newMachine, setNewMachine] = useState({
    name: '',
    tank_volumes_ml: [12000],
    loss_margin_pct: 5
  });
  const [selectedCountry, setSelectedCountry] = useState(getUserCountry());
  const [selectedLanguage, setSelectedLanguage] = useState(getUserLanguage());
  const [detectingCountry, setDetectingCountry] = useState(false);
  const [devices, setDevices] = useState([]);
  const [deviceLimits, setDeviceLimits] = useState({ current: 0, max: 1 });
  const [currentTourStep, setCurrentTourStep] = useState(-1);
  const [isAdminSectionExpanded, setIsAdminSectionExpanded] = useState(true);

  useEffect(() => {
    fetchData();
    fetchDevices();
    // Update selected country and language from localStorage when component mounts
    setSelectedCountry(getUserCountry());
    setSelectedLanguage(getUserLanguage());
  }, [sessionId]);

  // Start tour for first-time users
  useEffect(() => {
    if (hasPremiumAccess(user) && !isTourCompleted(TOUR_KEYS.SETTINGS, user)) {
      setTimeout(() => {
        setCurrentTourStep(0);
      }, 1000);
    }
  }, [user]);

  const handleTourNext = () => {
    setCurrentTourStep(prev => prev + 1);
  };

  const handleTourSkip = () => {
    markTourCompleted(TOUR_KEYS.SETTINGS, API, updateCompletedTours);
    setCurrentTourStep(-1);
  };

  const handleTourFinish = () => {
    markTourCompleted(TOUR_KEYS.SETTINGS, API, updateCompletedTours);
    setCurrentTourStep(-1);
    toast.success(t('settings.settingsTourComplete'));
  };
  
  const fetchDevices = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/auth/devices`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setDevices(response.data.devices);
      setDeviceLimits({
        current: response.data.current_count,
        max: response.data.max_devices
      });
    } catch (error) {
      console.error('Error fetching devices:', error);
    }
  };
  
  const logoutDevice = async (deviceId, sessionToken) => {
    try {
      const token = localStorage.getItem('session_token');
      
      // Build request body - prefer device_id, fallback to session_token
      const requestBody = deviceId 
        ? { device_id: deviceId }
        : { session_token: sessionToken };
      
      await axios.post(`${API}/auth/devices/logout`, 
        requestBody,
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        }
      );
      toast.success('Enhed logget ud');
      fetchDevices();
    } catch (error) {
      console.error('Logout device error:', error);
      toast.error('Kunne ikke logge enhed ud');
    }
  };
  
  const logoutAllDevices = async () => {
    if (!window.confirm('Log ud fra alle andre enheder?')) return;
    
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.post(`${API}/auth/devices/logout-all`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success(`${response.data.count} enheder logget ud`);
      fetchDevices();
    } catch (error) {
      toast.error('Kunne ikke logge ud fra enheder');
    }
  };

  const fetchData = async () => {
    try {
      const [machinesRes, limitsRes] = await Promise.all([
        axios.get(`${API}/machines/${sessionId}`),
        axios.get(`${API}/user/${sessionId}/limits`, {
          withCredentials: true
        })
      ]);
      setMachines(machinesRes.data);
      setUserRecipesCount(limitsRes.data.user_recipes_count);
      setCanAddRecipe(limitsRes.data.can_add_recipe);
      
      // Translate limit message
      let message = limitsRes.data.limit_message;
      if (message.includes('__UNLIMITED_RECIPES__')) {
        const role = message.split(':')[0];
        message = `${role}: ${t('settings.unlimitedRecipes')}`;
      }
      setLimitMessage(message);
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const addMachine = async (e) => {
    e.preventDefault();
    if (!newMachine.name) {
      toast.error('Indtast maskine navn');
      return;
    }

    try {
      if (isEditMode) {
        // Update existing machine
        await axios.put(`${API}/machines/${editMachineId}`, {
          session_id: sessionId,
          ...newMachine
        });
        toast.success('Maskine opdateret!');
      } else {
        // Add new machine
        await axios.post(`${API}/machines`, {
          session_id: sessionId,
          ...newMachine
        });
        toast.success('Maskine tilføjet!');
      }
      
      setIsAddMachineOpen(false);
      setIsEditMode(false);
      setEditMachineId(null);
      setNewMachine({
        name: '',
        tank_volumes_ml: [12000],
        loss_margin_pct: 5
      });
      fetchData();
    } catch (error) {
      console.error('Error saving machine:', error);
      toast.error(isEditMode ? 'Kunne ikke opdatere maskine' : 'Kunne ikke tilføje maskine');
    }
  };

  const handleEditMachine = (machine) => {
    setIsEditMode(true);
    setEditMachineId(machine.id);
    setNewMachine({
      name: machine.name,
      tank_volumes_ml: machine.tank_volumes_ml,
      loss_margin_pct: machine.loss_margin_pct
    });
    setIsAddMachineOpen(true);
  };

  const handleDeleteMachine = async (machineId) => {
    if (!window.confirm('Er du sikker på, at du vil slette denne maskine?')) {
      return;
    }

    try {
      await axios.delete(`${API}/machines/${machineId}?session_id=${sessionId}`);
      toast.success('Maskine slettet!');
      fetchData();
    } catch (error) {
      console.error('Error deleting machine:', error);
      toast.error('Kunne ikke slette maskine');
    }
  };

  const handleCancelMachine = () => {
    setIsAddMachineOpen(false);
    setIsEditMode(false);
    setEditMachineId(null);
    setNewMachine({
      name: '',
      tank_volumes_ml: [12000],
      loss_margin_pct: 5
    });
  };

  const presetMachines = [
    { name: 'SPM Pictor 6L', volumes: [6000] },
    { name: 'SPM Pictor 12L', volumes: [12000] },
    { name: 'SPM Pictor 2x12L', volumes: [12000, 12000] },
    { name: 'UGOLINI 10L', volumes: [10000] },
    { name: 'Ninja', volumes: [1890] },
    { name: 'Casper Sobzyk', volumes: [3000] },
    { name: 'Custom 6L', volumes: [6000] }
  ];

  return (
    <div className="space-y-6 fade-in" data-testid="settings-page">
      {/* Onboarding Tour */}
      <OnboardingTooltip
        steps={getSettingsPageSteps()}
        currentStep={currentTourStep}
        onNext={handleTourNext}
        onSkip={handleTourSkip}
        onFinish={handleTourFinish}
      />
      
      <div>
        <h1 className="text-4xl font-bold mb-2">{t('settings.title')}</h1>
        <p className="text-gray-600">{t('settings.subtitle')}</p>
      </div>

      {/* User Info */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
        <h2 className="text-2xl font-bold mb-4">{t('settings.yourAccount')}</h2>
        <div className="space-y-3">
          {user && (
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
              <span className="text-gray-700">{t('settings.user')}</span>
              <span className="text-sm font-semibold">{user.name}</span>
            </div>
          )}
          {!user && (
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
              <span className="text-gray-700">Session ID</span>
              <span className="text-sm font-mono text-gray-500">{sessionId.slice(0, 8)}...</span>
            </div>
          )}
          <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
            <span className="text-gray-700">{t('settings.myRecipes')}</span>
            <span className="font-semibold">
              {userRecipesCount} {(isAdmin() || isPro() || isEditor()) ? `(${t('settings.unlimited')})` : '/ 2'}
            </span>
          </div>
          <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
            <span className="text-gray-700">{t('settings.status')}</span>
            <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
              isAdmin() ? 'bg-red-100 text-red-700' :
              isEditor() ? 'bg-purple-100 text-purple-700' :
              isPro() ? 'bg-green-100 text-green-700' :
              'bg-cyan-100 text-cyan-700'
            }`}>
              {user
                ? (isAdmin() ? 'Admin' : isEditor() ? 'Redaktør' : (getUserSubscription(user)?.name || 'Free'))
                : 'Gæst'}
            </span>
          </div>
        </div>
      </div>

      {/* Device Management */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold">{t('settings.devices')}</h2>
            <p className="text-sm text-gray-600">
              {t('settings.deviceLimit', { current: deviceLimits.current, max: deviceLimits.max })}
            </p>
          </div>
          {devices.length > 1 && (
            <Button
              onClick={logoutAllDevices}
              variant="outline"
              className="text-red-600 hover:text-red-700"
            >
              Log ud fra alle andre
            </Button>
          )}
        </div>
        
        {devices.length === 0 ? (
          <p className="text-gray-500">{t('settings.noActiveDevices')}</p>
        ) : (
          <div className="space-y-3">
            {devices.map((device, index) => (
              <div
                key={index}
                className={`p-4 rounded-lg border-2 ${
                  device.is_current ? 'border-green-500 bg-green-50' : 'border-gray-200'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-bold text-gray-800">
                        {device.device_name}
                      </h3>
                      {device.is_current && (
                        <span className="text-xs bg-green-500 text-white px-2 py-1 rounded-full">
                          {t('settings.currentDevice')}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500">
                      {t('settings.lastActive')}: {new Date(device.last_active).toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-400">
                      IP: {device.ip_address}
                    </p>
                  </div>
                  {!device.is_current && (
                    <Button
                      onClick={() => logoutDevice(device.device_id, device.session_token)}
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:text-red-700"
                      disabled={!device.device_id && !device.session_token}
                    >
                      Log ud
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        
        <div className="mt-4 p-3 bg-blue-50 rounded-lg text-sm text-gray-700">
          <strong>💡 {t('common.tip')}:</strong> {deviceLimits.max === 1 
            ? t('settings.freeDeviceLimit')
            : t('settings.deviceInfo', { max: deviceLimits.max })}
          {deviceLimits.max < 3 && (
            <span> {t('settings.upgradeForMore')}</span>
          )}
        </div>
      </div>

      {/* Pro subscription section */}
      <SubscriptionSection />

      {/* Country & Language Settings (gated by feature flag: show_language_selector) */}
      {isFeatureEnabled('show_language_selector') && (
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
        <div className="flex items-center gap-2 mb-4">
          <FaGlobe className="text-blue-500 text-xl" />
          <h2 className="text-2xl font-bold">{t('settings.countryAndLanguage')}</h2>
        </div>
        <p className="text-gray-600 mb-4">
          {t('settings.languageDesc')}
        </p>
        
        <div className="space-y-4">
          {/* Combined Language Selector */}
          <div>
            <Label className="text-sm font-medium mb-2 block">{t('settings.selectLanguage')}</Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {Object.entries(LANGUAGES).map(([code, language]) => (
                <button
                  key={code}
                  onClick={async () => {
                    setSelectedLanguage(code);
                    // Set country based on language (map language to country)
                    const countryMapping = {
                      'da': 'dk',
                      'de': 'de',
                      'en': 'gb',
                      'en_us': 'us',
                      'fr': 'fr'
                    };
                    const countryCode = countryMapping[code] || 'dk';
                    setSelectedCountry(countryCode);
                    
                    await setUserLanguage(code);
                    await updateUserPreferences(countryCode, code);
                    
                    // Change i18n language without reloading (preserves login session!)
                    await i18n.changeLanguage(code);
                    
                    toast.success(t('messages.success.languageChanged', { language: language.name }));
                  }}
                  className={`flex items-center gap-2 p-3 rounded-lg border-2 transition-all ${
                    selectedLanguage === code
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <span className="text-2xl">{language.flag}</span>
                  <span className="text-sm font-medium">{language.name}</span>
                </button>
              ))}
            </div>
          </div>
          
          <div className="flex gap-2">
            <Button
              onClick={async () => {
                setDetectingCountry(true);
                try {
                  const result = await refreshUserLocation();
                  const detectedLang = COUNTRIES[result.country_code]?.lang || 'da';
                  setSelectedCountry(result.country_code);
                  setSelectedLanguage(detectedLang);
                  await setUserLanguage(detectedLang);
                  toast.success(`${t('settings.language')}: ${LANGUAGES[detectedLang]?.name || detectedLang}`);
                  setTimeout(() => {
                    window.location.reload();
                  }, 500);
                } catch (error) {
                  toast.error(t('messages.error.generic'));
                } finally {
                  setDetectingCountry(false);
                }
              }}
              disabled={detectingCountry}
              variant="outline"
              className="flex-1"
            >
              {detectingCountry ? t('common.loading') : `🔄 ${t('settings.autoDetect', 'Auto-detect')}`}
            </Button>
          </div>
          
          <div className="p-3 bg-blue-50 rounded-lg text-sm text-gray-700 mt-4">
            <strong>💡 {t('common.tip', 'Tip')}:</strong> {t('settings.countryTip', 'Dit land bruges til at vise relevante produktlinks når du klikker på "Indkøb" knapper.')}
          </div>
        </div>
      </div>
      )}

      {/* Onboarding Tour Reset */}
      {hasPremiumAccess(user) && (
        <div className="bg-yellow-50 rounded-2xl p-6 shadow-sm border border-yellow-200 mt-6">
          <div className="flex items-center gap-2 mb-4">
            <FaQuestionCircle className="text-yellow-600 text-xl" />
            <h2 className="text-2xl font-bold">{t('settings.guides')}</h2>
          </div>
          
          {/* Tips & Tricks Link */}
          <Link
            to="/tips"
            className="block p-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-xl hover:shadow-lg transition-all mb-4"
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-bold text-lg flex items-center gap-2">
                  💡 {t('settings.tipsAndTricks')}
                </h3>
                <p className="text-cyan-50 text-sm mt-1">
                  {t('settings.tipsAndTricksDesc')}
                </p>
              </div>
              <span className="text-2xl">→</span>
            </div>
          </Link>

          <p className="text-gray-700 mb-4">
            {t('settings.guidesDesc')}
          </p>
          
          {/* Reset All Tours Button */}
          <Button
            onClick={async () => {
              await resetAllTours(API);
              toast.success(t('settings.guidesReset'));
            }}
            data-tour="restart-tours-button"
            className="bg-yellow-500 hover:bg-yellow-600 text-white mb-4 w-full"
          >
            🔄 {t('settings.restartAllGuides')}
          </Button>

          {/* Individual Tour Reset Buttons */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-4">
            <button
              onClick={async () => {
                await resetTour(TOUR_KEYS.HOME, API);
                toast.success(t('settings.tourHomeReset'));
              }}
              className="px-4 py-2 bg-white border-2 border-yellow-300 rounded-lg hover:bg-yellow-100 text-sm font-medium transition-colors"
            >
              🏠 {t('settings.tourHome')}
            </button>
            <button
              onClick={() => {
                resetTour(TOUR_KEYS.RECIPES);
                toast.success(t('settings.tourRecipesReset'));
              }}
              className="px-4 py-2 bg-white border-2 border-yellow-300 rounded-lg hover:bg-yellow-100 text-sm font-medium transition-colors"
            >
              📖 {t('settings.tourRecipes')}
            </button>
            <button
              onClick={() => {
                resetTour(TOUR_KEYS.ADD_RECIPE);
                toast.success(t('settings.tourAddRecipeReset'));
              }}
              className="px-4 py-2 bg-white border-2 border-yellow-300 rounded-lg hover:bg-yellow-100 text-sm font-medium transition-colors"
            >
              ➕ {t('settings.tourAddRecipe')}
            </button>
            <button
              onClick={() => {
                resetTour(TOUR_KEYS.MATCH);
                toast.success(t('settings.tourMatchFinderReset'));
              }}
              className="px-4 py-2 bg-white border-2 border-yellow-300 rounded-lg hover:bg-yellow-100 text-sm font-medium transition-colors"
            >
              🔍 {t('settings.tourMatchFinder')}
            </button>
            <button
              onClick={() => {
                resetTour(TOUR_KEYS.SHOPPING_LIST);
                toast.success(t('settings.tourShoppingListReset'));
              }}
              className="px-4 py-2 bg-white border-2 border-yellow-300 rounded-lg hover:bg-yellow-100 text-sm font-medium transition-colors"
            >
              🛒 {t('settings.tourShoppingList')}
            </button>
            <button
              onClick={() => {
                resetTour(TOUR_KEYS.SETTINGS);
                toast.success(t('settings.tourSettingsReset'));
              }}
              className="px-4 py-2 bg-white border-2 border-yellow-300 rounded-lg hover:bg-yellow-100 text-sm font-medium transition-colors"
            >
              ⚙️ {t('settings.tourSettings')}
            </button>
            <button
              onClick={() => {
                resetTour(TOUR_KEYS.AI_ASSISTANT);
                toast.success(t('settings.tourAIReset'));
              }}
              className="px-4 py-2 bg-white border-2 border-yellow-300 rounded-lg hover:bg-yellow-100 text-sm font-medium transition-colors"
            >
              🤖 {t('settings.tourAI')}
            </button>
          </div>
        </div>
      )}

      {/* My Recipes */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold">{t('settings.myRecipes')}</h2>
          <Link to="/add-recipe">
            <Button
              disabled={!canAddRecipe}
              data-testid="add-recipe-button"
              className="bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700"
            >
              <FaPlus className="mr-2" /> {t('settings.addRecipe')}
            </Button>
          </Link>
        </div>
        <p className="text-gray-600">
          {isAdmin() || isPro() || isEditor()
            ? t(userRecipesCount === 1 ? 'settings.youHaveCreated' : 'settings.youHaveCreatedPlural', { count: userRecipesCount })
            : canAddRecipe
            ? t((2 - userRecipesCount) === 1 ? 'settings.youCanAdd' : 'settings.youCanAddPlural', { count: 2 - userRecipesCount })
            : t('settings.freeLimitReached')}
        </p>
      </div>

      {/* Machines */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100" data-tour="machine-section">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold">{t('settings.myMachines')}</h2>
          {user ? (
            <Dialog open={isAddMachineOpen} onOpenChange={setIsAddMachineOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-machine-button" data-tour="add-machine-button" variant="outline">
                  <FaPlus className="mr-2" /> {t('settings.addMachine')}
                </Button>
              </DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{isEditMode ? t('settings.editMachine') : t('settings.addMachine')}</DialogTitle>
              </DialogHeader>
              <form onSubmit={addMachine} className="space-y-4">
                <div>
                  <Label>Maskine Navn</Label>
                  <Input
                    data-testid="machine-name-input"
                    value={newMachine.name}
                    onChange={(e) => setNewMachine({...newMachine, name: e.target.value})}
                    placeholder="fx SPM Pictor 12L"
                  />
                </div>
                {!isEditMode && (
                  <div>
                    <Label>Hurtig Valg</Label>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      {presetMachines.map((preset, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => setNewMachine({
                            name: preset.name,
                            tank_volumes_ml: preset.volumes,
                            loss_margin_pct: 5
                          })}
                          className="px-3 py-2 bg-cyan-50 text-cyan-700 rounded-lg hover:bg-cyan-100 text-sm"
                        >
                          {preset.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  <Label>{t('settings.containerSize')}</Label>
                  <Input
                    type="number"
                    data-testid="tank-volume-input"
                    value={newMachine.tank_volumes_ml[0]}
                    onChange={(e) => setNewMachine({
                      ...newMachine,
                      tank_volumes_ml: [parseInt(e.target.value)]
                    })}
                  />
                </div>
                <div>
                  <Label>{t('settings.lossMargin')}</Label>
                  <Input
                    type="number"
                    value={newMachine.loss_margin_pct}
                    onChange={(e) => setNewMachine({
                      ...newMachine,
                      loss_margin_pct: parseFloat(e.target.value)
                    })}
                  />
                </div>
                <div className="flex space-x-2">
                  <Button 
                    type="button" 
                    variant="outline" 
                    className="flex-1"
                    onClick={handleCancelMachine}
                  >
                    Annuller
                  </Button>
                  <Button 
                    type="submit" 
                    className="flex-1" 
                    data-testid="submit-machine"
                  >
                    {isEditMode ? t('settings.saveChanges') : t('settings.addMachine')}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          ) : (
            <Button 
              onClick={() => toast.info('Log ind for at tilføje maskiner')}
              variant="outline"
              className="opacity-50 cursor-not-allowed"
              disabled
            >
              <FaPlus className="mr-2" /> {t('settings.addMachine')} (Kun medlemmer)
            </Button>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-6">
            <div className="loading-spinner"></div>
          </div>
        ) : machines.length === 0 ? (
          <p className="text-gray-600">{t('settings.noMachinesYet')}</p>
        ) : (
          <div className="space-y-3">
            {machines.map((machine) => (
              <div
                key={machine.id}
                data-testid={`machine-${machine.id}`}
                className="p-4 bg-gradient-to-r from-cyan-50 to-blue-50 rounded-lg"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="font-bold text-lg">{machine.name}</h3>
                    <p className="text-sm text-gray-600">
                      {t('settings.container')}: {machine.tank_volumes_ml.map(v => `${v/1000}L`).join(', ')}
                      {' • '}
                      {t('settings.loss')}: {machine.loss_margin_pct}%
                    </p>
                    {machine.is_default && (
                      <span className="inline-block mt-2 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">
                        ✓ {t('settings.default')}
                      </span>
                    )}
                  </div>
                  {!machine.is_system && user && (
                    <div className="flex space-x-2 ml-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditMachine(machine)}
                        data-testid={`edit-machine-${machine.id}`}
                        className="hover:bg-cyan-100"
                      >
                        <FaEdit className="text-cyan-600" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteMachine(machine.id)}
                        data-testid={`delete-machine-${machine.id}`}
                        className="hover:bg-red-100"
                      >
                        <FaTrash className="text-red-600" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* About */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
        <h2 className="text-2xl font-bold mb-4">{t('settings.aboutTitle')}</h2>
        <div className="space-y-4 text-gray-700">
          <p>
            {t('settings.aboutIntro')}
          </p>
          
          <div className="pl-4">
            <p className="font-semibold mb-2">{t('settings.youCan')}</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>{t('settings.matchIngredients')}</li>
              <li>{t('settings.scaleAmounts')}</li>
              <li>{t('settings.saveFavorites')}</li>
              <li>{t('settings.shareRecipes')}</li>
            </ul>
          </div>
          
          <p>
            {t('settings.appDevelopment')}
          </p>
          
          <div className="pt-2">
            <Link 
              to="/guide" 
              data-tour="guide-link"
              className="inline-flex items-center gap-2 text-cyan-600 hover:text-cyan-700 font-semibold hover:underline"
            >
              <FaBook /> {t('settings.readFullGuide')} →
            </Link>
          </div>
        </div>
        
        <div className="mt-6 pt-4 border-t border-gray-200">
          <p className="text-sm text-gray-500">
            {t('settings.version')}
          </p>
        </div>
      </div>

      {/* Moderator Section — only for editors (admins see the full Admin block below). */}
      {user && user.role === 'editor' && (
        <div className="bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-2xl shadow-lg overflow-hidden">
          <div className="p-6">
            <h2 className="text-2xl font-bold mb-1">Moderation</h2>
            <p className="text-white/90">Moderér brugeropskrifter</p>
          </div>
          <div className="bg-white p-4 space-y-3">
            <Link
              to="/admin/sandbox"
              data-testid="settings-sandbox-link-editor"
              className="block p-4 bg-purple-50 rounded-lg hover:shadow-md transition-all border border-purple-100"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-gray-800">🔍 {t('settings.sandbox', 'Sandkasse')}</h3>
                  <p className="text-sm text-gray-600">{t('settings.approveRejectRecipes', 'Godkend eller afvis brugeropskrifter')}</p>
                </div>
                <span className="text-purple-600">→</span>
              </div>
            </Link>
          </div>
        </div>
      )}

      {/* Administrator Section - Only for admin */}
      {user && user.role === 'admin' && (
        <div className="bg-gradient-to-r from-red-500 to-pink-500 text-white rounded-2xl shadow-lg overflow-hidden">
          {/* Clickable Header */}
          <button
            onClick={() => setIsAdminSectionExpanded(!isAdminSectionExpanded)}
            className="w-full p-6 text-left hover:bg-black/10 transition-colors flex items-center justify-between"
          >
            <div>
              <h2 className="text-2xl font-bold mb-1">{t('settings.administrator')}</h2>
              <p className="text-white/90">{t('settings.adminTools')}</p>
            </div>
            {isAdminSectionExpanded ? (
              <FaChevronUp className="text-white text-xl" />
            ) : (
              <FaChevronDown className="text-white text-xl" />
            )}
          </button>
        </div>
      )}
      
      {user && user.role === 'admin' && isAdminSectionExpanded && (
        <div className="space-y-3">
          <Link
            to="/admin/comments"
            className="block p-4 bg-white rounded-lg hover:shadow-md transition-all border border-blue-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-bold text-gray-800 flex items-center gap-2">
                  <FaComments className="text-blue-500" />
                  💬 {t('settings.comments')}
                </h3>
                <p className="text-sm text-gray-600">{t('settings.moderateComments')}</p>
              </div>
              <span className="text-blue-600">→</span>
            </div>
          </Link>
          
          <Link
            to="/admin/sandbox"
            className="block p-4 bg-white rounded-lg hover:shadow-md transition-all border border-purple-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-bold text-gray-800">🔍 {t('settings.sandbox')}</h3>
                <p className="text-sm text-gray-600">{t('settings.approveRejectRecipes')}</p>
              </div>
              <span className="text-purple-600">→</span>
            </div>
          </Link>
          <Link
            to="/admin/ingredients"
            className="block p-4 bg-white rounded-lg hover:shadow-md transition-all border border-green-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-bold text-gray-800">🥤 {t('settings.ingredients')}</h3>
                <p className="text-sm text-gray-600">{t('settings.manageMasterIngredients')}</p>
              </div>
              <span className="text-green-600">→</span>
            </div>
          </Link>
          <Link
            to="/admin"
            className="block p-4 bg-white rounded-lg hover:shadow-md transition-all border border-red-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-bold text-gray-800">{t('settings.adminPanel')}</h3>
                <p className="text-sm text-gray-600">{t('settings.csvImportVendor')}</p>
              </div>
              <span className="text-red-600">→</span>
            </div>
          </Link>
          <Link
            to="/admin/links"
            className="block p-4 bg-white rounded-lg hover:shadow-md transition-all border border-red-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-bold text-gray-800">{t('settings.productLinks')}</h3>
                <p className="text-sm text-gray-600">{t('settings.manageSupplierLinks')}</p>
              </div>
              <span className="text-red-600">→</span>
            </div>
          </Link>
          <Link
            to="/admin/badges"
            className="block p-4 bg-white rounded-lg hover:shadow-md transition-all border border-yellow-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-bold text-gray-800 flex items-center gap-2">
                  <span>🏆</span> {t('settings.badgeManagement')}
                </h3>
                <p className="text-sm text-gray-600">{t('settings.manageUserBadges')}</p>
              </div>
              <span className="text-yellow-600">→</span>
            </div>
          </Link>
          <Link
            to="/admin/translations"
            className="block p-4 bg-white rounded-lg hover:shadow-md transition-all border border-blue-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-bold text-gray-800 flex items-center gap-2">
                  <FaGlobe className="text-blue-500" />
                  🌍 UI Oversættelser
                </h3>
                <p className="text-sm text-gray-600">Rediger UI tekster (knapper, labels, beskeder)</p>
              </div>
              <span className="text-blue-600">→</span>
            </div>
          </Link>
          <Link
            to="/admin/recipe-translations"
            className="block p-4 bg-white rounded-lg hover:shadow-md transition-all border border-purple-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-bold text-gray-800 flex items-center gap-2">
                  <FaBook className="text-purple-500" />
                  📖 Opskrift Oversættelser
                </h3>
                <p className="text-sm text-gray-600">Oversæt opskrifter (beskrivelser og trin)</p>
              </div>
              <span className="text-purple-600">→</span>
            </div>
          </Link>
          <Link
            to="/admin/import-recipes"
            className="block p-4 bg-white rounded-lg hover:shadow-md transition-all border border-green-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-bold text-gray-800 flex items-center gap-2">
                  <FaUpload className="text-green-500" />
                  📥 Import Opskrifter
                </h3>
                <p className="text-sm text-gray-600">Upload JSON fil med opskrifter og oversættelser</p>
              </div>
              <span className="text-green-600">→</span>
            </div>
          </Link>
        </div>
      )}

      {/* Bruger Platformen Section - Only for admin */}
      {user && user.role === 'admin' && (
        <div className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-2xl p-6 shadow-lg mt-6">
          <h2 className="text-2xl font-bold mb-1">{t('settings.userPlatform')}</h2>
          <p className="text-white/90 mb-4">{t('settings.viewAllUsers')}</p>
        </div>
      )}
      
      {user && user.role === 'admin' && (
        <div className="space-y-3">
          <Link
            to="/members"
            className="block p-4 bg-white rounded-lg hover:shadow-md transition-all border border-blue-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-bold text-gray-800">👥 {t('settings.members')}</h3>
                <p className="text-sm text-gray-600">{t('settings.viewAllUsers')}</p>
              </div>
              <span className="text-blue-600">→</span>
            </div>
          </Link>
        </div>
      )}
    </div>
  );
};

export default SettingsPage;